/**
 * <s-product-gallery> — PDP image carousel with arrows and thumbnails.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --prev   scroll to the previous slide
 *   --next   scroll to the next slide
 *   --go     scroll to the slide in the source button's data-index
 *
 * Required markup:
 *   [data-gallery-track]  — horizontally scrolling, snap-mandatory track
 *   [data-gallery-slide]  — each slide inside the track
 *   [data-gallery-thumb]  — optional thumbnail buttons, in slide order
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons
 */
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  #handleCommand = (event) => {
    if (event.command === '--prev') this.goTo(this.#index - 1);
    else if (event.command === '--next') this.goTo(this.#index + 1);
    else if (event.command === '--go') this.goTo(Number(event.source?.dataset.index ?? 0));
  };

  goTo(index) {
    const slides = this.#slides;
    const track = this.#track;
    if (!track || slides.length === 0) return;

    const target = Math.max(0, Math.min(index, slides.length - 1));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slides[target].offsetLeft - track.offsetLeft;
    track.scrollTo({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#update(target);
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const track = this.#track;
      if (!track || track.clientWidth === 0) return;
      const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
      this.#update(index);
    });
  };

  // Scrolls only the thumbnail strip, never the page.
  #revealThumb(thumb) {
    const strip = thumb.closest('[data-gallery-thumbs]');
    if (!strip) return;
    const left = thumb.offsetLeft - strip.offsetLeft;
    const right = left + thumb.offsetWidth;
    if (left < strip.scrollLeft) strip.scrollLeft = left;
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth;
  }

  #update(index) {
    const count = this.#slides.length;
    this.#index = Math.max(0, Math.min(index, count - 1));

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.#index === 0;
    if (next) next.disabled = this.#index >= count - 1;

    const thumbs = this.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb, i) => {
      if (i === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
